/**
 * Which kind of IFS each chat is about, for the sidebar: once a workspace has chats on both IFS Cloud and
 * IFS Applications 10, the list is split under those two headings (the same words as the tags in Apps & Sync),
 * most recent first inside each. On one kind only, nothing changes. The gateway notes the environment when a
 * chat is created or prompted, and reads older chats off the tools they used.
 */
import { createSignal } from "solid-js"
import { NGC_WEB } from "./mode"

export type SessionKind = "cloud" | "apps10"
type Answer = { sessions: Record<string, { kind: SessionKind; name: string }>; kinds: number }

export const KIND_LABEL: Record<SessionKind, string> = { apps10: "IFS Applications 10", cloud: "IFS Cloud" }

const [known, setKnown] = createSignal<Record<string, { kind: SessionKind; name: string }>>({})
let last = ""
let inflight: Promise<void> | null = null

/** Ask the gateway about these chats (the ones on screen); answers land in `known`. */
export function ngcRefreshSessionKinds(ids: string[]): void {
  if (!NGC_WEB || !ids.length) return
  const key = ids.join(",")
  if (key === last || inflight) return
  last = key
  inflight = fetch(`/web/sessions/envs?ids=${encodeURIComponent(key)}`, {
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" },
  })
    .then((r) => (r.ok ? (r.json() as Promise<Answer>) : null))
    .then((a) => {
      if (a) setKnown(a.sessions)
    })
    .catch(() => {})
    .finally(() => {
      inflight = null
    })
}

/** A chat's kind, when the gateway knows it. */
export function ngcSessionKind(id: string): SessionKind | undefined {
  return known()[id]?.kind
}

/**
 * The chats grouped for the sidebar, or null when there is nothing to split: fewer than two kinds among them.
 * Groups come in the order their first chat appears; chats of no known kind yet close the list.
 */
export function ngcGroupSessions<T extends { id: string }>(sessions: T[]): { label: string; sessions: T[] }[] | null {
  if (!NGC_WEB) return null
  const map = known()
  const groups = new Map<string, { label: string; sessions: T[] }>()
  const rest: T[] = []
  for (const s of sessions) {
    const kind = map[s.id]?.kind
    if (!kind) {
      rest.push(s)
      continue
    }
    const g = groups.get(kind) ?? { label: KIND_LABEL[kind], sessions: [] }
    g.sessions.push(s)
    groups.set(kind, g)
  }
  if (groups.size < 2) return null
  return [...groups.values(), ...(rest.length ? [{ label: "Other chats", sessions: rest }] : [])]
}
